/**
 * Rehype plugin that highlights all occurrences of a search query in the HAST
 * (HTML AST) by wrapping matching text spans in <mark data-find-match> elements.
 *
 * Usage with ReactMarkdown:
 *   rehypePlugins={[rehypeRaw, query ? [rehypeHighlightSearch, { query }] : []]}
 */
import { visit } from "unist-util-visit";
import type { Root, Text, Element } from "hast";

interface Options {
  query?: string;
}

export function rehypeHighlightSearch(options?: Options) {
  const query = options?.query?.trim() ?? "";
  if (!query) return () => {};

  const lowerQuery = query.toLowerCase();

  return (tree: Root) => {
    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || index === null || index === undefined) return;

      // Don't double-highlight text already inside a <mark>
      if (
        parent.type === "element" &&
        (parent as Element).tagName === "mark"
      ) {
        return;
      }

      const text = node.value;
      const lowerText = text.toLowerCase();

      if (!lowerText.includes(lowerQuery)) return;

      const parts: Array<Text | Element> = [];
      let lastIndex = 0;
      let matchStart: number;

      while (
        (matchStart = lowerText.indexOf(lowerQuery, lastIndex)) !== -1
      ) {
        // Text before the match
        if (matchStart > lastIndex) {
          parts.push({ type: "text", value: text.slice(lastIndex, matchStart) });
        }

        // The match wrapped in <mark>
        parts.push({
          type: "element",
          tagName: "mark",
          properties: { "data-find-match": "" },
          children: [
            {
              type: "text",
              value: text.slice(matchStart, matchStart + query.length),
            },
          ],
        } as Element);

        lastIndex = matchStart + query.length;
      }

      // Remaining text after last match
      if (lastIndex < text.length) {
        parts.push({ type: "text", value: text.slice(lastIndex) });
      }

      // Replace the original text node with the split parts
      (parent.children as Array<Text | Element>).splice(
        index as number,
        1,
        ...parts
      );
    });
  };
}
